import type {
  ApiResponseBody,
  ApiSearchQuery,
} from '@/lib/api/api-type-helper';
import { apiClient } from '@/lib/api/client';
import { getDataOrThrowApiError } from '@/lib/api/error';
import { orpcClient } from '@/lib/api/orpc/client';
import type {
  ListEgovApplicationStepsByTagInput,
  ListEgovApplicationStepsByTagOutput,
  ListEgovApplicationStepsByTagRowOutput,
} from '@/lib/api/orpc/contracts/labor-core-egov-application-steps';
import type {
  CountYearEndAdjustmentInstancesByTagDocumentTabsInput,
  CountYearEndAdjustmentInstancesByTagDocumentTabsOutput,
  ListYearEndAdjustmentInstancesByTagInput,
  ListYearEndAdjustmentInstancesByTagOutput,
  ListYearEndAdjustmentInstancesByTagRowOutput,
} from '@/lib/api/orpc/contracts/labor-core-year-end-adjustment-instances';

export const OWNER_PRODUCTS = [
  'HRF_LABOR_CORE',
  'HRF_CONTRACTOR_CORE',
  'IT_OPERATION_CORE',
  'GA_OPERATION_CORE',
] as const;

export type OwnerProduct = (typeof OWNER_PRODUCTS)[number];

// --- Get Operation Packages ---

export type OperationPackagesResponse = ApiResponseBody<
  '/v2/boe/packages/{ownerProduct}',
  'get'
>;

export type PackageType = OperationPackagesResponse[number]['packageType'];

export const getOperationPackages = async (
  ownerProduct: OwnerProduct,
): Promise<OperationPackagesResponse> => {
  const res = await apiClient.GET('/v2/boe/packages/{ownerProduct}', {
    params: {
      path: { ownerProduct },
    },
  });
  return getDataOrThrowApiError(res);
};

// --- Get Operation Package Count In Progress ---

export type OperationPackageCountInProgressResponse = ApiResponseBody<
  '/v2/boe/packages/{ownerProduct}/{id}/count-in-progress',
  'get'
>;

export const getOperationPackageCountInProgress = async (
  ownerProduct: OwnerProduct,
  id: string,
): Promise<OperationPackageCountInProgressResponse> => {
  const res = await apiClient.GET(
    '/v2/boe/packages/{ownerProduct}/{id}/count-in-progress',
    {
      params: {
        path: { ownerProduct, id },
      },
    },
  );
  return getDataOrThrowApiError(res);
};

// --- Get Operation Package Patterns ---

export type OperationPackagePatternsResponse = ApiResponseBody<
  '/v2/boe/packages/{ownerProduct}/{id}/patterns',
  'get'
>;

export const getOperationPackagePatterns = async (
  ownerProduct: OwnerProduct,
  id: string,
): Promise<OperationPackagePatternsResponse> => {
  const res = await apiClient.GET(
    '/v2/boe/packages/{ownerProduct}/{id}/patterns',
    {
      params: {
        path: { ownerProduct, id },
      },
    },
  );
  return getDataOrThrowApiError(res);
};

// --- Post Operation Package Patterns ---

export type PostOperationPackagePatternsResponse = ApiResponseBody<
  '/v2/boe/packages/{ownerProduct}/{id}/patterns',
  'post'
>;

export const postOperationPackagePatterns = async (
  ownerProduct: OwnerProduct,
  id: string,
  body?: { name: string; description?: string },
): Promise<PostOperationPackagePatternsResponse> => {
  const res = await apiClient.POST(
    '/v2/boe/packages/{ownerProduct}/{id}/patterns',
    {
      params: {
        path: { ownerProduct, id },
      },
      body: body ?? {},
    },
  );
  return getDataOrThrowApiError(res);
};

// --- Assign Task To Person ---

export type StartPatternResponse = ApiResponseBody<
  '/v2/boe/packages/{ownerProduct}/{id}/patterns/{patternId}',
  'post'
>;

export type StartPatternRequestBody = {
  targetPersonIds: string[];
  anchorDate: string;
  /** タグ名（手続きインスタンスに付与する運用ラベル） */
  tagName?: string;
};

export const startPattern = async (
  ownerProduct: OwnerProduct,
  id: string,
  patternId: string,
  body: StartPatternRequestBody,
): Promise<StartPatternResponse> => {
  const res = await apiClient.POST(
    '/v2/boe/packages/{ownerProduct}/{id}/patterns/{patternId}',
    {
      params: {
        path: { ownerProduct, id, patternId },
      },
      body,
    },
  );
  return getDataOrThrowApiError(res);
};

// --- Get Operation Task Count In Progress ---

export type OperationTaskCountInProgressResponse = ApiResponseBody<
  '/v2/boe/packages/{ownerProduct}/{id}/patterns/{patternId}/count-in-progress',
  'get'
>;

export const getOperationTaskCountInProgress = async (
  ownerProduct: OwnerProduct,
  id: string,
  patternId: string,
): Promise<OperationTaskCountInProgressResponse> => {
  const res = await apiClient.GET(
    '/v2/boe/packages/{ownerProduct}/{id}/patterns/{patternId}/count-in-progress',
    {
      params: {
        path: { ownerProduct, id, patternId },
      },
    },
  );
  return getDataOrThrowApiError(res);
};

// --- Get Target Persons (it-operation-core: 対象者一覧。デバイス手配・アカウント一括発行で利用) ---

const IT_OPERATION_TARGET_PERSONS_PATH =
  '/v2/it-operation-core/packages/{packageId}/patterns/{patternId}/target-persons' as const;

export type ItOperationTargetPersonsResponse = ApiResponseBody<
  typeof IT_OPERATION_TARGET_PERSONS_PATH,
  'get'
>;

export type ItOperationTargetPersonsQuery = ApiSearchQuery<
  typeof IT_OPERATION_TARGET_PERSONS_PATH,
  'get'
>;

export const getItOperationTargetPersons = async (
  packageId: string,
  patternId: string,
  query: ItOperationTargetPersonsQuery,
): Promise<ItOperationTargetPersonsResponse> => {
  const res = await apiClient.GET(IT_OPERATION_TARGET_PERSONS_PATH, {
    params: {
      path: { packageId, patternId },
      query,
    },
  });
  return getDataOrThrowApiError(res);
};

// --- Start Labor Core Pattern (supports both targetPersonIds and targetContractIds) ---

export type StartLaborCorePatternResponse = ApiResponseBody<
  '/v2/labor-core/operation-packages/{id}/patterns/{patternId}',
  'post'
>;

export type StartLaborCorePatternRequestBody = {
  targetPersonIds?: string[];
  targetContractIds?: string[];
  anchorDate: string;
  /** タグ名（手続きインスタンスに付与する運用ラベル） */
  tagName?: string;
};

export const startLaborCorePattern = async (
  id: string,
  patternId: string,
  body: StartLaborCorePatternRequestBody,
): Promise<StartLaborCorePatternResponse> => {
  const res = await apiClient.POST(
    '/v2/labor-core/operation-packages/{id}/patterns/{patternId}',
    {
      params: {
        path: { id, patternId },
      },
      body,
    },
  );
  return getDataOrThrowApiError(res);
};

// --- Start Contractor Core Pattern (supports both targetPersonIds and targetContractIds) ---

export type StartContractorCorePatternResponse = ApiResponseBody<
  '/v2/contractor-core/operation-packages/{id}/patterns/{patternId}',
  'post'
>;

export type StartContractorCorePatternRequestBody = {
  targetPersonIds?: string[];
  targetContractIds?: string[];
  anchorDate: string;
  /** タグ名（手続きインスタンスに付与する運用ラベル） */
  tagName?: string;
};

export const startContractorCorePattern = async (
  id: string,
  patternId: string,
  body: StartContractorCorePatternRequestBody,
): Promise<StartContractorCorePatternResponse> => {
  const res = await apiClient.POST(
    '/v2/contractor-core/operation-packages/{id}/patterns/{patternId}',
    {
      params: {
        path: { id, patternId },
      },
      body,
    },
  );
  return getDataOrThrowApiError(res);
};

// --- Get Operation Package Instances ---

export type OperationPackageInstancesResponse = ApiResponseBody<
  '/v2/boe/packages/{ownerProduct}/instances',
  'get'
>;

export type OperationPackageInstanceRow =
  OperationPackageInstancesResponse['items'][number];

export const getOperationPackageInstances = async (
  ownerProduct: OwnerProduct,
  patternId: string,
  options?: { page?: number; pageSize?: number; targetId?: string },
): Promise<OperationPackageInstancesResponse> => {
  const res = await apiClient.GET('/v2/boe/packages/{ownerProduct}/instances', {
    params: {
      path: { ownerProduct },
      query: { patternId, ...options },
    },
  });
  return getDataOrThrowApiError(res);
};

/**
 * Labor 行政申請（手続き系）の Operation Package Instance 一覧（Step 列付き）。
 * バックエンド: `GET /boe/packages/:ownerProduct/instances/with-step-columns`
 * （クエリは `GET .../instances` と同様。OpenAPI 上は `/v2` プレフィックス付き）
 */
const GOVERNMENT_APPLICATION_PACKAGE_INSTANCES_GET =
  '/v2/boe/packages/{ownerProduct}/instances/with-step-columns' as const;

export type GovernmentApplicationPackageInstancesResponse = ApiResponseBody<
  typeof GOVERNMENT_APPLICATION_PACKAGE_INSTANCES_GET,
  'get'
>;

export const getGovernmentApplicationPackageInstances = async (
  ownerProduct: OwnerProduct,
  patternId: string,
  options?: { page?: number; pageSize?: number; targetId?: string },
): Promise<GovernmentApplicationPackageInstancesResponse> => {
  const res = await apiClient.GET(
    GOVERNMENT_APPLICATION_PACKAGE_INSTANCES_GET,
    {
      params: {
        path: { ownerProduct },
        query: { patternId, ...options },
      },
    },
  );
  return getDataOrThrowApiError(res);
};

/**
 * Labor Core 専用: タグ別・行政申請系パッケージの Step 一覧（確認画面用）。
 * oRPC: `listEgovApplicationStepsByTagContract`
 */
export type EgovApplicationStepsByTagResponse =
  ListEgovApplicationStepsByTagOutput;

export type EgovApplicationStepByTagRow =
  ListEgovApplicationStepsByTagRowOutput;

export type EgovApplicationStepsByTagQuery = Omit<
  ListEgovApplicationStepsByTagInput,
  'tagId'
>;

export const getEgovApplicationStepsByTag = async (
  tagId: string,
  options: EgovApplicationStepsByTagQuery,
): Promise<EgovApplicationStepsByTagResponse> => {
  return orpcClient.laborCoreEgovApplicationSteps.listByTag({
    tagId,
    page: options.page,
    pageSize: options.pageSize,
    orderBy: options.orderBy,
    orderDirection: options.orderDirection,
    searchText: options.searchText,
    status: options.status,
  });
};

/**
 * Labor Core: 年末調整 内容確認（タグ別インスタンス一覧）。
 * oRPC: `listYearEndAdjustmentInstancesByTagContract`
 */
export type YearEndAdjustmentInstancesByTagResponse =
  ListYearEndAdjustmentInstancesByTagOutput;

export type YearEndAdjustmentInstanceByTagRow =
  ListYearEndAdjustmentInstancesByTagRowOutput;

export type YearEndAdjustmentInstancesByTagQuery = Omit<
  ListYearEndAdjustmentInstancesByTagInput,
  'tagId'
>;

export const getYearEndAdjustmentInstancesByTag = async (
  tagId: string,
  options: YearEndAdjustmentInstancesByTagQuery,
): Promise<YearEndAdjustmentInstancesByTagResponse> => {
  return orpcClient.laborCoreYearEndAdjustmentInstances.listByTag({
    tagId,
    page: options.page,
    pageSize: options.pageSize,
    orderBy: options.orderBy,
    orderDirection: options.orderDirection,
    searchText: options.searchText,
    status: options.status,
    documentFocus: options.documentFocus,
  });
};

/**
 * Labor Core: 年末調整 内容確認（書類タブ別件数）。
 * oRPC: `countYearEndAdjustmentInstancesByTagDocumentTabsContract`
 */
export type YearEndAdjustmentDocumentTabCountsResponse =
  CountYearEndAdjustmentInstancesByTagDocumentTabsOutput;

export type YearEndAdjustmentDocumentTabCountsQuery = Omit<
  CountYearEndAdjustmentInstancesByTagDocumentTabsInput,
  'tagId'
>;

export const getYearEndAdjustmentDocumentTabCounts = async (
  tagId: string,
  options: YearEndAdjustmentDocumentTabCountsQuery,
): Promise<YearEndAdjustmentDocumentTabCountsResponse> => {
  return orpcClient.laborCoreYearEndAdjustmentInstances.documentTabCounts({
    tagId,
    searchText: options.searchText,
    status: options.status,
  });
};

const PACKAGE_INSTANCE_TAG_GET =
  '/v2/boe/packages/{ownerProduct}/instance-tags/{tagId}' as const;

export type PackageInstanceTagResponse = ApiResponseBody<
  typeof PACKAGE_INSTANCE_TAG_GET,
  'get'
>;

export const getPackageInstanceTag = async (
  ownerProduct: OwnerProduct,
  tagId: string,
): Promise<PackageInstanceTagResponse> => {
  const res = await apiClient.GET(PACKAGE_INSTANCE_TAG_GET, {
    params: {
      path: { ownerProduct, tagId },
    },
  });
  return getDataOrThrowApiError(res);
};
